/**
 * Shared state for the impersonation bar. Non-component exports only, so
 * react-refresh keeps working in `ImpersonationBar.tsx` (same split as
 * `orgHierarchy.ts` / `orgColumns.tsx`).
 */

import { useAuthStore } from "@/store/authStore";
import { isImpersonated } from "@/lib/auth/jwt";

/** Height of the bar, and the amount the app shifts down while it is shown. */
export const IMPERSONATION_BAR_HEIGHT = "3rem";

/**
 * The CSS variable the layout reads to make room for the bar.
 *
 * Set on `<html>` rather than passed as a prop because the sidebar is `fixed`
 * and so cannot be pushed down by a parent's padding — it has to offset its own
 * `top`. A variable lets the bar, the sidebar and the main shell agree without
 * threading a boolean through three components.
 */
export const IMPERSONATION_BAR_HEIGHT_VAR = "--impersonation-bar-h";

/**
 * Whether the impersonation bar is showing.
 *
 * One rule, used by both the bar and anything that reserves space for it. The
 * AND-gate below is subtle enough that a hand-copied second version would
 * eventually disagree, and a layout that reserves space while the bar is hidden
 * leaves a band of dead space across the top of every page.
 */
export function useImpersonationVisible(): boolean {
  const impersonation = useAuthStore((s) => s.impersonation);
  const accessToken = useAuthStore((s) => s.accessToken);

  // Defensive AND-gate against state/JWT desync: hide the bar if the store
  // claims impersonation but the access token's `is_impersonated` claim does
  // not agree (catches the cached-token bug class this surface fixed).
  const jwtSaysImpersonated = accessToken ? isImpersonated(accessToken) : false;
  return impersonation.isImpersonating && jwtSaysImpersonated;
}
